export * from "./mtfUtils";
export * from "./api";
export * from "./constants";
export * from "./customHooks";
export * from "./protectRoute";
export * from "./quizUtils";
export * from "./speechUtils";
export * from "./studyUtils";
export * from "./chapterUtils";

import React from "react";
import { compile } from "html-to-text";
import dayjs from "dayjs";

// Timezone-aware "now" as an ISO-8601 string that carries the local UTC offset
// (e.g. 2026-06-10T20:00:00.000+05:30) instead of new Date().toISOString()'s
// always-UTC "...Z". Use this for activity start/end timestamps so the recorded
// time reflects the learner's local clock.
export const nowISO = () => dayjs().format("YYYY-MM-DDTHH:mm:ss.SSSZ");

export const addLeadingZero = (number) => {
  if (number > 9) {
    return number;
  } else {
    return "0" + number;
  }
};

// utility function to format the remaining time as minutes:seconds
export function formatTime(time) {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${addLeadingZero(minutes)} : ${addLeadingZero(seconds)}`;
  return (
    <>
      <span style={{ fontSize: "26px", fontWeight: "600" }}>{minutes}</span> m :{" "}
      <span style={{ fontSize: "26px", fontWeight: "600" }}>
        {seconds.toString().padStart(2, "0")}
      </span>{" "}
      s
    </>
  );
}

export const convertSeconds = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const hourString = hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const minuteString = minutes > 0 ? `${minutes}` : "";
  const secondString =
    remainingSeconds > 0
      ? `${remainingSeconds} second${remainingSeconds > 1 ? "s" : ""}`
      : "";

  if (hours > 0) {
    return `${hourString} : ${minuteString || "0 minute"} ${
      secondString && `: ${secondString}`
    }`;
  } else if (!hours && minutes > 0) {
    return `${minuteString} ${secondString && `: ${secondString}`}`;
  }

  return secondString;
};

export const refreshPage = () => {
  window.location.reload();
};

export const shuffleArray = (array) => {
  const shuffledArray = [...array];

  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    // Swap elements using array destructuring
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }

  return shuffledArray;
};

export const getYouTubeVideoId = (url = "") => {
  var regExp =
    /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  var match = String(url || "").match(regExp);

  if (match && match[1].length === 11) {
    return match[1];
  } else {
    return null;
  }
};

export const slugify = (text = "") => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w-]+/g, "") // Remove all non-word characters
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
};

export const generateRandomColor = () => {
  // Generate random values for red, green, and blue components with higher bounds
  var red = Math.floor(Math.random() * 150) + 140; // Random number between 140 and 250 so that the colors are on lighter shades
  var green = Math.floor(Math.random() * 150) + 140;
  var blue = Math.floor(Math.random() * 150) + 140;

  if (red > 250 && blue > 250 && green > 250) {
    // If the color generated is more closer to white, recalculate the numbers
    return generateRandomColor();
  }

  if ([red, green, blue].filter((color) => color > 250).length > 1) {
    // If any two of the color values is greater than 255, recalculate the numbers
    return generateRandomColor();
  }

  // Combine RGB values to form a CSS color string
  // var color = "rgb(" + red + ", " + green + ", " + blue + ")";
  var color = "rgba(255, 199, 74, 0.25)";

  return color;
};

export const generateUUID = () => {
  var chars = "0123456789abcdef";
  var uuid = "";
  for (var i = 0; i < 32; i++) {
    var index = Math.floor(Math.random() * chars.length);
    uuid += chars[index];
    if (i === 7 || i === 11 || i === 15 || i === 19) {
      uuid += "-";
    }
  }
  uuid = uuid.slice(0, 14) + "4" + uuid.slice(15, 3) + "y" + uuid.slice(19);
  return uuid;
};

export const parseParams = (endpoint, params) => {
  if (!endpoint || !params) {
    return endpoint;
  }
  if (!typeof params === "object") {
    params = {};
  }
  if (!Object.keys(params).length) {
    return endpoint;
  }

  return endpoint.replace(/{{(.*?)}}/g, (match, paramName) => {
    if (!params[paramName]) {
      return "";
    }
    return params[paramName];
  });
};

export const imageFileFilter = (filename) => {
  if (!filename) return;
  return filename.match(/\.(jpg|jpeg|png|gif)$/i);
};

export const videoFileFilter = (filename) => {
  if (!filename) return;
  return filename.match(/\.(mp4|mov|wmv|avi|mkv)$/i);
};

export const audioFileFilter = (filename) => {
  if (!filename) return;
  return filename.match(/\.(mp3|wav)$/i);
};

export const deepClone = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  let clone = Array.isArray(obj) ? [] : {};

  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      clone[key] = deepClone(obj[key]);
    }
  }

  return clone;
};

export const htmlToText = (htmlContent) => {
  return compile()(htmlContent);
};

export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const stringToColor = (string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

export const stringAvatar = (name, withColorProps = false) => {
  if (!name) return "";
  let splitName = name.split(" ");

  let children =
    `${splitName[0][0]}${splitName.length > 1 ? splitName[1][0] : ""}`.toUpperCase();
  if (!withColorProps) return children;

  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children,
  };
};

export const generateCourseCode = () => {
  const randomNumber = Math.floor(1000 + Math.random() * 9000); // Ensures a 4-digit number (1000-9999)
  const year = new Date().getFullYear(); // Gets the current year
  return `IR-${year}-${randomNumber}`;
};
